import React, { useEffect, useRef, useMemo } from "react";
import { StormcloudVideoPlayer } from "../player/StormcloudVideoPlayer";
import type { StormcloudVideoPlayerConfig } from "../types";
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaVolumeDown,
  FaExpand,
  FaCompress,
  FaSpinner,
} from "react-icons/fa";

export type StormcloudVideoPlayerProps = Omit<
  StormcloudVideoPlayerConfig,
  "videoElement"
> &
  React.VideoHTMLAttributes<HTMLVideoElement> & {
    onReady?: (player: StormcloudVideoPlayer) => void;
    wrapperClassName?: string;
    wrapperStyle?: React.CSSProperties;
    licenseKey?: string;
  };

const CRITICAL_PROPS = [
  "src",
  "allowNativeHls",
  "licenseKey",
  "lowLatencyMode",
  "driftToleranceMs",
  "vastMode",
] as const;

export const StormcloudVideoPlayerComponent: React.FC<StormcloudVideoPlayerProps> =
  React.memo(
    (props) => {
      const {
        src,
        autoplay,
        muted,
        lowLatencyMode,
        allowNativeHls,
        driftToleranceMs,
        immediateManifestAds,
        debugAdTiming,
        showCustomControls,
        onVolumeToggle,
        onFullscreenToggle,
        onControlClick,
        onReady,
        wrapperClassName,
        wrapperStyle,
        className,
        style,
        controls,
        playsInline,
        preload,
        poster,
        children,
        licenseKey,
        vastMode,
        vastTagUrl,
        adPlayerType,
        minSegmentsBeforePlay,
        ...restVideoAttrs
      } = props;

      const videoRef = useRef<HTMLVideoElement | null>(null);
      const playerRef = useRef<StormcloudVideoPlayer | null>(null);
      const bufferingTimeoutRef = useRef<number | null>(null);
      const [adStatus, setAdStatus] = React.useState<{
        showAds: boolean;
        currentIndex: number;
        totalAds: number;
      }>({ showAds: false, currentIndex: 0, totalAds: 0 });

      const [shouldShowNativeControls, setShouldShowNativeControls] =
        React.useState(true);

      const [isMuted, setIsMuted] = React.useState(false);
      const [isFullscreen, setIsFullscreen] = React.useState(false);
      const [isPlaying, setIsPlaying] = React.useState(false);
      const [currentTime, setCurrentTime] = React.useState(0);
      const [duration, setDuration] = React.useState(0);
      const [volume, setVolume] = React.useState(1);
      const [playbackRate, setPlaybackRate] = React.useState(1);
      const [showVolumeSlider, setShowVolumeSlider] = React.useState(false);
      const [showSpeedMenu, setShowSpeedMenu] = React.useState(false);
      const [isLoading, setIsLoading] = React.useState(true);
      const [isBuffering, setIsBuffering] = React.useState(false);
      const [showCenterPlay, setShowCenterPlay] = React.useState(false);
      const [showLicenseWarning, setShowLicenseWarning] = React.useState(false);
      const [viewportWidth, setViewportWidth] = React.useState(
        typeof window !== "undefined" ? window.innerWidth : 1920
      );
      const [isPortrait, setIsPortrait] = React.useState(
        typeof window !== "undefined"
          ? window.innerHeight > window.innerWidth
          : false
      );

      const getResponsiveScale = () => {
        if (viewportWidth < 480) return 0.7;
        if (viewportWidth < 768) return 0.8;
        if (viewportWidth < 1024) return 0.9;
        return 1;
      };

      const responsiveScale = getResponsiveScale();

      const formatTime = (seconds: number): string => {
        if (!isFinite(seconds)) return "0:00:00";
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${hours}:${minutes
          .toString()
          .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
      };

      const handlePlayPause = () => {
        if (videoRef.current) {
          if (videoRef.current.paused) {
            const hasValidSource =
              videoRef.current.src ||
              (videoRef.current.currentSrc &&
                videoRef.current.currentSrc !== "") ||
              videoRef.current.readyState >= 1;

            if (hasValidSource) {
              videoRef.current.play()?.catch((error) => {
                console.error("[StormcloudVideoPlayer] Failed to play:", error);
              });
              setShowCenterPlay(false);
            } else {
              console.warn(
                "[StormcloudVideoPlayer] Cannot play: video has no valid source"
              );
            }
          } else {
            videoRef.current.pause();
            setShowCenterPlay(true);
          }
        }
      };

      const handleCenterPlayClick = () => {
        if (videoRef.current && videoRef.current.paused) {
          const hasValidSource =
            videoRef.current.src ||
            (videoRef.current.currentSrc &&
              videoRef.current.currentSrc !== "") ||
            videoRef.current.readyState >= 1;

          if (hasValidSource) {
            videoRef.current.play()?.catch((error) => {
              console.error("[StormcloudVideoPlayer] Failed to play:", error);
            });
            setShowCenterPlay(false);
          } else {
            console.warn(
              "[StormcloudVideoPlayer] Cannot play: video has no valid source"
            );
          }
        }
      };

      const handleTimelineSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        if (videoRef.current && duration > 0 && isFinite(duration)) {
          const rect = e.currentTarget.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const progress = Math.max(0, Math.min(1, clickX / rect.width));
          const newTime = progress * duration;

          if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
            videoRef.current.currentTime = newTime;
          }
        }
      };

      const handleVolumeChange = (newVolume: number) => {
        if (playerRef.current && isFinite(newVolume)) {
          const clampedVolume = Math.max(0, Math.min(1, newVolume));
          playerRef.current.setVolume(clampedVolume);
        }
      };

      const handlePlaybackRateChange = (rate: number) => {
        if (videoRef.current && isFinite(rate) && rate > 0) {
          videoRef.current.playbackRate = rate;
        }
        setShowSpeedMenu(false);
      };

      const isHlsStream =
        src?.toLowerCase().includes(".m3u8") ||
        src?.toLowerCase().includes("/hls/");
      const shouldShowEnhancedControls =
        showCustomControls && (isHlsStream ? allowNativeHls : true);

      const criticalPropsKey = useMemo(() => {
        return CRITICAL_PROPS.map((prop) => `${prop}:${props[prop]}`).join("|");
      }, [
        src,
        allowNativeHls,
        licenseKey,
        lowLatencyMode,
        driftToleranceMs,
        vastMode,
      ]);

      useEffect(() => {
        if (typeof window === "undefined") return;
        const el = videoRef.current;
        if (!el || !src) return;

        if (!licenseKey) {
          setShowLicenseWarning(true);
          setIsLoading(false);
          console.warn(
            "StormcloudVideoPlayer: License key is required but not provided. Please set the licenseKey prop to use the player."
          );
          return;
        }

        setShowLicenseWarning(false);

        if (debugAdTiming) {
          console.log("[StormcloudUI] Initializing player, isLoading=true");
        }

        if (playerRef.current) {
          try {
            playerRef.current.destroy();
          } catch {}
          playerRef.current = null;
        }

        const cfg: StormcloudVideoPlayerConfig = {
          src,
          videoElement: el,
        } as StormcloudVideoPlayerConfig;
        if (autoplay !== undefined) cfg.autoplay = autoplay;
        if (muted !== undefined) cfg.muted = muted;
        if (lowLatencyMode !== undefined) cfg.lowLatencyMode = lowLatencyMode;
        if (allowNativeHls !== undefined) cfg.allowNativeHls = allowNativeHls;
        if (driftToleranceMs !== undefined)
          cfg.driftToleranceMs = driftToleranceMs;
        if (immediateManifestAds !== undefined)
          cfg.immediateManifestAds = immediateManifestAds;
        if (debugAdTiming !== undefined) cfg.debugAdTiming = debugAdTiming;
        if (showCustomControls !== undefined)
          cfg.showCustomControls = showCustomControls;
        if (onVolumeToggle !== undefined) cfg.onVolumeToggle = onVolumeToggle;
        if (onFullscreenToggle !== undefined)
          cfg.onFullscreenToggle = onFullscreenToggle;
        if (onControlClick !== undefined) cfg.onControlClick = onControlClick;
        if (licenseKey !== undefined) cfg.licenseKey = licenseKey;
        if (vastMode !== undefined) cfg.vastMode = vastMode;
        if (vastTagUrl !== undefined) cfg.vastTagUrl = vastTagUrl;
        if (adPlayerType !== undefined) cfg.adPlayerType = adPlayerType;
        if (minSegmentsBeforePlay !== undefined)
          cfg.minSegmentsBeforePlay = minSegmentsBeforePlay;

        const player = new StormcloudVideoPlayer(cfg);
        playerRef.current = player;
        player
          .load()
          .then(() => {
            const showNative = player.shouldShowNativeControls();
            setShouldShowNativeControls(showNative);
            if (debugAdTiming) {
              console.log(
                "[StormcloudUI] Player loaded successfully, waiting for video ready"
              );
            }
            onReady?.(player);
          })
          .catch((error) => {
            console.error(
              "StormcloudVideoPlayer: Failed to load player:",
              error
            );
            setIsLoading(false);
            onReady?.(player);
          });

        return () => {
          try {
            player.destroy();
          } catch {}
          playerRef.current = null;
        };
      }, [criticalPropsKey]);

      useEffect(() => {
        if (!playerRef.current) return;

        try {
          if (autoplay !== undefined && playerRef.current.videoElement) {
            playerRef.current.videoElement.autoplay = autoplay;
          }
          // Only apply muted prop when NOT showing ads to prevent interference with ad audio
          if (muted !== undefined && !playerRef.current.isShowingAds()) {
            playerRef.current.setMuted(muted);
          }
        } catch (error) {
          console.warn("Failed to update player properties:", error);
        }
      }, [autoplay, muted]);

      useEffect(() => {
        if (!playerRef.current) return;

        const checkAdStatus = () => {
          if (playerRef.current) {
            const showAds = playerRef.current.isShowingAds();
            const currentIndex = playerRef.current.getCurrentAdIndex();
            const totalAds = playerRef.current.getTotalAdsInBreak();

            setAdStatus((prev) => {
              if (
                prev.showAds !== showAds ||
                prev.currentIndex !== currentIndex ||
                prev.totalAds !== totalAds
              ) {
                return { showAds, currentIndex, totalAds };
              }
              return prev;
            });
          }
        };

        const interval = setInterval(checkAdStatus, 100);
        return () => clearInterval(interval);
      }, []);

      useEffect(() => {
        if (typeof window === "undefined" || !playerRef.current) return;

        const handleResize = () => {
          if (playerRef.current && videoRef.current) {
            if (typeof playerRef.current.resize === "function") {
              playerRef.current.resize();
            }
          }
          setViewportWidth(window.innerWidth);
          setIsPortrait(window.innerHeight > window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
      }, []);

      useEffect(() => {
        if (!playerRef.current || !videoRef.current) return;

        const updateStates = () => {
          if (playerRef.current && videoRef.current) {
            setIsMuted(playerRef.current.isMuted());
            setIsPlaying(!videoRef.current.paused);

            const currentTimeValue = videoRef.current.currentTime;
            setCurrentTime(isFinite(currentTimeValue) ? currentTimeValue : 0);

            const durationValue = videoRef.current.duration;
            setDuration(isFinite(durationValue) ? durationValue : 0);

            const volumeValue = videoRef.current.volume;
            setVolume(
              isFinite(volumeValue) ? Math.max(0, Math.min(1, volumeValue)) : 1
            );

            const rateValue = videoRef.current.playbackRate;
            setPlaybackRate(
              isFinite(rateValue) && rateValue > 0 ? rateValue : 1
            );
          }
          setIsFullscreen(
            document.fullscreenElement === videoRef.current?.parentElement
          );
        };

        const interval = setInterval(updateStates, 200);

        const handleFullscreenChange = () => {
          setIsFullscreen(
            document.fullscreenElement === videoRef.current?.parentElement
          );
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () => {
          clearInterval(interval);
          document.removeEventListener(
            "fullscreenchange",
            handleFullscreenChange
          );
        };
      }, []);

      useEffect(() => {
        if (!videoRef.current) return;

        const handleLoadedMetadata = () => {
          if (videoRef.current) {
            const video = videoRef.current;
            void video.offsetHeight;
          }
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: loadedmetadata, readyState:",
              videoRef.current?.readyState
            );
          }
        };

        const handleLoadedData = () => {
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: loadeddata, readyState:",
              videoRef.current?.readyState
            );
          }
        };

        const handleLoadStart = () => {
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: loadstart, readyState:",
              videoRef.current?.readyState
            );
          }
        };

        const handleCanPlay = () => {
          setIsLoading(false);
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
          }
          setIsBuffering(false);
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: canplay, readyState:",
              videoRef.current?.readyState,
              "- clearing loading state, isLoading=false"
            );
          }
        };

        const handleCanPlayThrough = () => {
          setIsLoading(false);
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
          }
          setIsBuffering(false);
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: canplaythrough, readyState:",
              videoRef.current?.readyState,
              "- clearing loading state, isLoading=false"
            );
          }
        };

        const handleWaiting = () => {
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
          }

          bufferingTimeoutRef.current = window.setTimeout(() => {
            setIsBuffering(true);
            if (debugAdTiming) {
              console.log(
                "[StormcloudUI] Video buffering detected (after 300ms delay), readyState:",
                videoRef.current?.readyState,
                "- showing spinner, isBuffering=true"
              );
            }
          }, 300);

          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: waiting, readyState:",
              videoRef.current?.readyState,
              "- buffering delay started (300ms)"
            );
          }
        };

        const handlePlaying = () => {
          setIsLoading(false);
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
          }
          setIsBuffering(false);
          setShowCenterPlay(false);
          if (debugAdTiming) {
            console.log(
              "[StormcloudUI] Video event: playing, readyState:",
              videoRef.current?.readyState,
              "- playback started, isLoading=false, isBuffering=false"
            );
          }
        };

        const handlePause = () => {
          if (playerRef.current && !playerRef.current.isShowingAds()) {
            setShowCenterPlay(true);
          } else {
            setShowCenterPlay(false);
          }
        };

        const handleEnded = () => {
          setShowCenterPlay(true);
        };

        const video = videoRef.current;
        video.addEventListener("loadstart", handleLoadStart);
        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.addEventListener("loadeddata", handleLoadedData);
        video.addEventListener("canplay", handleCanPlay);
        video.addEventListener("canplaythrough", handleCanPlayThrough);
        video.addEventListener("waiting", handleWaiting);
        video.addEventListener("playing", handlePlaying);
        video.addEventListener("pause", handlePause);
        video.addEventListener("ended", handleEnded);

        if (video.paused) {
          setShowCenterPlay(true);
        }

        return () => {
          if (bufferingTimeoutRef.current) {
            clearTimeout(bufferingTimeoutRef.current);
            bufferingTimeoutRef.current = null;
          }

          video.removeEventListener("loadstart", handleLoadStart);
          video.removeEventListener("loadedmetadata", handleLoadedMetadata);
          video.removeEventListener("loadeddata", handleLoadedData);
          video.removeEventListener("canplay", handleCanPlay);
          video.removeEventListener("canplaythrough", handleCanPlayThrough);
          video.removeEventListener("waiting", handleWaiting);
          video.removeEventListener("playing", handlePlaying);
          video.removeEventListener("pause", handlePause);
          video.removeEventListener("ended", handleEnded);
        };
      }, [debugAdTiming]);

      return (
        <>
          <style>
            {`
              @keyframes spin {
                from {
                  transform: rotate(0deg);
                }
                to {
                  transform: rotate(360deg);
                }
              }
              
              .stormcloud-video-wrapper:fullscreen {
                border-radius: 0 !important;
                box-shadow: none !important;
                width: 100vw !important;
                height: 100vh !important;
                max-width: 100vw !important;
                max-height: 100vh !important;
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                z-index: 999999 !important;
                background: #000 !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
              }
              
              .stormcloud-video-wrapper:has(*:fullscreen) {
                border-radius: 0 !important;
                box-shadow: none !important;
                width: 100vw !important;
                height: 100vh !important;
                max-width: 100vw !important;
                max-height: 100vh !important;
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                z-index: 999999 !important;
                background: #000 !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
              }
              
              *:fullscreen {
                width: 100vw !important;
                height: 100vh !important;
                max-width: 100vw !important;
                max-height: 100vh !important;
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                z-index: 999999 !important;
                background: #000 !important;
              }
            `}
          </style>
          <div
            className={`stormcloud-video-wrapper ${wrapperClassName || ""}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: isFullscreen ? "fixed" : "relative",
              top: isFullscreen ? 0 : undefined,
              left: isFullscreen ? 0 : undefined,
              overflow: "hidden",
              width: isFullscreen ? "100vw" : "100%",
              height: isFullscreen ? "100vh" : "auto",
              minHeight: isFullscreen ? "100vh" : "auto",
              maxWidth: isFullscreen ? "100vw" : "100%",
              maxHeight: isFullscreen ? "100vh" : "none",
              zIndex: isFullscreen ? 999999 : undefined,
              backgroundColor: isFullscreen ? "#000" : undefined,
              borderRadius: isFullscreen ? 0 : undefined,
              boxShadow: isFullscreen ? "none" : undefined,
              ...wrapperStyle,
            }}
          >
            <video
              ref={videoRef}
              className={className}
              style={{
                display: "block",
                width: "100%",
                height: isFullscreen ? "100%" : "auto",
                maxWidth: "100%",
                maxHeight: isFullscreen ? "100%" : "none",
                objectFit: isFullscreen ? "cover" : "contain",
                backgroundColor: "#000",
                aspectRatio: isFullscreen ? "unset" : undefined,
                ...style,
              }}
              controls={
                shouldShowNativeControls && controls && !showCustomControls
              }
              playsInline={playsInline}
              preload={preload}
              poster={poster}
              {...restVideoAttrs}
            >
              {children}
            </video>

            {(isLoading || isBuffering) && (
              <FaSpinner
                size={42}
                color="white"
                style={{
                  position: "absolute",
                  top: "calc(50% - 21px)",
                  left: "calc(50% - 21px)",
                  zIndex: 20,
                  animation: "spin 1s linear infinite",
                  filter: "drop-shadow(0 3px 6px rgba(0, 0, 0, 0.8))",
                }}
              />
            )}

            {showLicenseWarning && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 25,
                  background:
                    "linear-gradient(135deg, rgba(220, 38, 38, 0.95) 0%, rgba(185, 28, 28, 0.9) 100%)",
                  color: "white",
                  padding: "24px 32px",
                  borderRadius: "16px",
                  backdropFilter: "blur(20px)",
                  border: "2px solid rgba(255, 255, 255, 0.2)",
                  boxShadow:
                    "0 20px 60px rgba(0, 0, 0, 0.6), inset 0 2px 0 rgba(255, 255, 255, 0.2)",
                  textAlign: "center",
                  maxWidth: "400px",
                  margin: "0 16px",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    marginBottom: "12px",
                    color: "#ffffff",
                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
                  }}
                >
                  License Key Required
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    lineHeight: "1.5",
                    color: "rgba(255, 255, 255, 0.9)",
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
                  }}
                >
                  Please provide a valid license key to use the Stormcloud Video
                  Player.
                  <br />
                  Contact your administrator for licensing information.
                </div>
              </div>
            )}

            {showCenterPlay &&
              !isLoading &&
              !isBuffering &&
              !showLicenseWarning &&
              !adStatus.showAds && (
                <div
                  onClick={handleCenterPlayClick}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 15,
                    cursor: "pointer",
                    background:
                      "linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(20, 20, 20, 0.8) 100%)",
                    borderRadius: "50%",
                    width: "100px",
                    height: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(20px)",
                    border: "3px solid rgba(255, 255, 255, 0.8)",
                    boxShadow:
                      "0 12px 40px rgba(0, 0, 0, 0.8), inset 0 2px 0 rgba(255, 255, 255, 0.3)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                  onMouseEnter={(e) => {
                    const target = e.currentTarget as HTMLElement;
                    target.style.transform = "translate(-50%, -50%)";
                    target.style.background =
                      "linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(40, 40, 40, 0.9) 100%)";
                    target.style.boxShadow =
                      "0 16px 48px rgba(0, 0, 0, 0.9), inset 0 2px 0 rgba(255, 255, 255, 0.4)";
                    target.style.borderColor = "rgba(255, 255, 255, 0.9)";
                  }}
                  onMouseLeave={(e) => {
                    const target = e.currentTarget as HTMLElement;
                    target.style.transform = "translate(-50%, -50%)";
                    target.style.background =
                      "linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(20, 20, 20, 0.8) 100%)";
                    target.style.boxShadow =
                      "0 12px 40px rgba(0, 0, 0, 0.8), inset 0 2px 0 rgba(255, 255, 255, 0.3)";
                    target.style.borderColor = "rgba(255, 255, 255, 0.8)";
                  }}
                  title="Play"
                >
                  <FaPlay
                    size={36}
                    color="white"
                    style={{
                      marginLeft: "6px",
                      filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8))",
                    }}
                  />
                </div>
              )}

            {shouldShowEnhancedControls && !showLicenseWarning ? (
              <>
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background:
                      "linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.8) 100%)",
                    padding: "20px 16px 16px",
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "8px",
                      background:
                        "linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      cursor: "pointer",
                      position: "relative",
                      backdropFilter: "blur(5px)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.2)",
                    }}
                    onClick={handleTimelineSeek}
                  >
                    <div
                      style={{
                        height: "100%",
                        background:
                          "linear-gradient(90deg, rgba(139, 92, 246, 0.9) 0%, rgba(59, 130, 246, 0.8) 50%, rgba(34, 197, 94, 0.9) 100%)",
                        borderRadius: "8px",
                        width: `${
                          duration > 0 ? (currentTime / duration) * 100 : 0
                        }%`,
                        transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        boxShadow: "0 2px 8px rgba(139, 92, 246, 0.4)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: "-6px",
                        right: `${
                          duration > 0
                            ? 100 - (currentTime / duration) * 100
                            : 100
                        }%`,
                        width: "20px",
                        height: "20px",
                        background:
                          "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 240, 240, 0.9) 100%)",
                        borderRadius: "50%",
                        border: "3px solid rgba(139, 92, 246, 0.8)",
                        boxShadow:
                          "0 4px 16px rgba(139, 92, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
                        transform: "translateX(50%)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      color: "white",
                      flexWrap: viewportWidth < 768 ? "wrap" : "nowrap",
                      gap: `${8 * responsiveScale}px`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: `${12 * responsiveScale}px`,
                        flexWrap: viewportWidth < 480 ? "wrap" : "nowrap",
                      }}
                    >
                      <button
                        onClick={handlePlayPause}
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.65) 100%)",
                          backdropFilter: "blur(12px)",
                          border: `${
                            2 * responsiveScale
                          }px solid rgba(255, 255, 255, 0.3)`,
                          color: "#ffffff",
                          cursor: "pointer",
                          padding: `${10 * responsiveScale}px`,
                          borderRadius: `${16 * responsiveScale}px`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow:
                            "0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                          minWidth: `${48 * responsiveScale}px`,
                          minHeight: `${48 * responsiveScale}px`,
                        }}
                        onMouseEnter={(e) => {
                          const target = e.target as HTMLElement;
                          target.style.background =
                            "linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 100%)";
                          target.style.boxShadow =
                            "0 12px 48px rgba(0, 0, 0, 0.6), 0 6px 24px rgba(0, 0, 0, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.4)";
                        }}
                        onMouseLeave={(e) => {
                          const target = e.target as HTMLElement;
                          target.style.background =
                            "linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.65) 100%)";
                          target.style.boxShadow =
                            "0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                        }}
                        title={isPlaying ? "Pause" : "Play"}
                      >
                        {isPlaying ? (
                          <FaPause
                            size={Math.max(16, 20 * responsiveScale)}
                            style={{ filter: "drop-shadow(0 0 0 transparent)" }}
                          />
                        ) : (
                          <FaPlay
                            size={Math.max(16, 20 * responsiveScale)}
                            style={{ filter: "drop-shadow(0 0 0 transparent)" }}
                          />
                        )}
                      </button>

                      <div
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          padding: "8px",
                          margin: "-8px",
                        }}
                        onMouseEnter={() => setShowVolumeSlider(true)}
                        onMouseLeave={() => setShowVolumeSlider(false)}
                      >
                        <button
                          onClick={() => {
                            if (playerRef.current) {
                              playerRef.current.toggleMute();
                            }
                            if (onVolumeToggle) {
                              onVolumeToggle();
                            }
                          }}
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.6) 100%)",
                            backdropFilter: "blur(10px)",
                            border: `${
                              2 * responsiveScale
                            }px solid rgba(255, 255, 255, 0.3)`,
                            color: "#ffffff",
                            cursor: "pointer",
                            padding: `${8 * responsiveScale}px`,
                            borderRadius: `${16 * responsiveScale}px`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            boxShadow:
                              "0 6px 28px rgba(0, 0, 0, 0.4), 0 3px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
                            minWidth: `${44 * responsiveScale}px`,
                            minHeight: `${44 * responsiveScale}px`,
                          }}
                          onMouseEnter={(e) => {
                            const target = e.target as HTMLElement;
                            target.style.background =
                              "linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.7) 100%)";
                            target.style.boxShadow =
                              "0 10px 36px rgba(0, 0, 0, 0.6), 0 5px 16px rgba(0, 0, 0, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.35)";
                          }}
                          onMouseLeave={(e) => {
                            const target = e.target as HTMLElement;
                            target.style.background =
                              "linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.6) 100%)";
                            target.style.boxShadow =
                              "0 6px 28px rgba(0, 0, 0, 0.4), 0 3px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25)";
                          }}
                          title={isMuted ? "Unmute" : "Mute"}
                        >
                          {isMuted || volume === 0 ? (
                            <FaVolumeMute
                              size={Math.max(14, 16 * responsiveScale)}
                              style={{
                                filter: "drop-shadow(0 0 0 transparent)",
                              }}
                            />
                          ) : volume < 0.5 ? (
                            <FaVolumeDown
                              size={Math.max(14, 16 * responsiveScale)}
                              style={{
                                filter: "drop-shadow(0 0 0 transparent)",
                              }}
                            />
                          ) : (
                            <FaVolumeUp
                              size={Math.max(14, 16 * responsiveScale)}
                              style={{
                                filter: "drop-shadow(0 0 0 transparent)",
                              }}
                            />
                          )}
                        </button>

                        {showVolumeSlider && (
                          <>
                            <div
                              style={{
                                position: "absolute",
                                bottom: "100%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "60px",
                                height: "20px",
                                marginBottom: "-16px",
                                zIndex: 9,
                              }}
                              onMouseEnter={() => setShowVolumeSlider(true)}
                              onMouseLeave={() => setShowVolumeSlider(false)}
                            />
                            <div
                              style={{
                                position: "absolute",
                                bottom: "100%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                marginBottom: "4px",
                                background:
                                  "linear-gradient(135deg, rgba(0, 0, 0, 0.88) 0%, rgba(20, 20, 20, 0.92) 100%)",
                                backdropFilter: "blur(15px)",
                                padding: "10px 14px",
                                borderRadius: "14px",
                                border: "1px solid rgba(255, 255, 255, 0.15)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "128px",
                                boxShadow:
                                  "0 12px 40px rgba(0, 0, 0, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)",
                                zIndex: 10,
                                transition:
                                  "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
                              }}
                              onMouseEnter={(e) => {
                                setShowVolumeSlider(true);
                                e.currentTarget.style.boxShadow =
                                  "0 16px 48px rgba(0, 0, 0, 0.6), 0 6px 16px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 0 24px rgba(59, 130, 246, 0.3)";
                                e.currentTarget.style.borderColor =
                                  "rgba(59, 130, 246, 0.4)";
                              }}
                              onMouseLeave={(e) => {
                                setShowVolumeSlider(false);
                                e.currentTarget.style.boxShadow =
                                  "0 12px 40px rgba(0, 0, 0, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)";
                                e.currentTarget.style.borderColor =
                                  "rgba(255, 255, 255, 0.15)";
                              }}
                            >
                              <div
                                style={{
                                  position: "relative",
                                  width: "8px",
                                  height: "104px",
                                  cursor: "pointer",
                                  transition: "transform 0.2s ease-in-out",
                                }}
                                onMouseEnter={(e) => {
                                  // Hover effect removed
                                }}
                                onMouseLeave={(e) => {
                                  // Hover effect removed
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  const sliderElement = e.currentTarget;

                                  const handleMouseMove = (
                                    moveEvent: MouseEvent
                                  ) => {
                                    if (!sliderElement) return;
                                    const rect =
                                      sliderElement.getBoundingClientRect();
                                    const y = moveEvent.clientY - rect.top;
                                    const percentage =
                                      1 -
                                      Math.max(0, Math.min(1, y / rect.height));
                                    handleVolumeChange(percentage);
                                  };

                                  const handleMouseUp = () => {
                                    document.removeEventListener(
                                      "mousemove",
                                      handleMouseMove
                                    );
                                    document.removeEventListener(
                                      "mouseup",
                                      handleMouseUp
                                    );
                                  };

                                  document.addEventListener(
                                    "mousemove",
                                    handleMouseMove
                                  );
                                  document.addEventListener(
                                    "mouseup",
                                    handleMouseUp
                                  );

                                  const rect =
                                    sliderElement.getBoundingClientRect();
                                  const y = e.clientY - rect.top;
                                  const percentage =
                                    1 -
                                    Math.max(0, Math.min(1, y / rect.height));
                                  handleVolumeChange(percentage);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rect =
                                    e.currentTarget.getBoundingClientRect();
                                  const y = e.clientY - rect.top;
                                  const percentage =
                                    1 -
                                    Math.max(0, Math.min(1, y / rect.height));
                                  handleVolumeChange(percentage);
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "0",
                                    left: "0",
                                    width: "100%",
                                    height: "100%",
                                    background:
                                      "linear-gradient(180deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.15) 100%)",
                                    borderRadius: "4px",
                                    boxShadow:
                                      "inset 0 1px 3px rgba(0, 0, 0, 0.2)",
                                  }}
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "0",
                                    left: "0",
                                    width: "100%",
                                    height: `${(isMuted ? 0 : volume) * 100}%`,
                                    background:
                                      "linear-gradient(180deg, rgba(96, 165, 250, 1) 0%, rgba(59, 130, 246, 0.95) 50%, rgba(37, 99, 235, 0.9) 100%)",
                                    borderRadius: "4px",
                                    transition:
                                      "height 0.15s ease-out, box-shadow 0.2s ease-in-out",
                                    boxShadow:
                                      "0 0 8px rgba(59, 130, 246, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                                  }}
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: `calc(${
                                      (isMuted ? 0 : volume) * 100
                                    }% - 7px)`,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "14px",
                                    height: "14px",
                                    background:
                                      "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                                    borderRadius: "50%",
                                    boxShadow:
                                      "0 2px 6px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(59, 130, 246, 0.3), 0 0 12px rgba(59, 130, 246, 0.4)",
                                    transition:
                                      "bottom 0.15s ease-out, transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, width 0.2s ease-in-out, height 0.2s ease-in-out",
                                    cursor: "grab",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                      "0 3px 10px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.6)";
                                    e.currentTarget.style.cursor = "grab";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow =
                                      "0 2px 6px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(59, 130, 246, 0.3), 0 0 12px rgba(59, 130, 246, 0.4)";
                                  }}
                                  onMouseDown={(e) => {
                                    e.currentTarget.style.cursor = "grabbing";
                                  }}
                                  onMouseUp={(e) => {
                                    e.currentTarget.style.cursor = "grab";
                                  }}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      <div
                        style={{
                          fontSize: `${14 * responsiveScale}px`,
                          fontFamily: "monospace",
                          color: "rgba(255, 255, 255, 0.9)",
                          display: viewportWidth < 480 ? "none" : "block",
                        }}
                      >
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: `${12 * responsiveScale}px`,
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          display: viewportWidth < 600 ? "none" : "block",
                        }}
                      >
                        <button
                          onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.6) 100%)",
                            backdropFilter: "blur(10px)",
                            border: `${
                              2 * responsiveScale
                            }px solid rgba(255, 255, 255, 0.3)`,
                            color: "#ffffff",
                            cursor: "pointer",
                            padding: `${8 * responsiveScale}px ${
                              14 * responsiveScale
                            }px`,
                            borderRadius: `${14 * responsiveScale}px`,
                            fontSize: `${14 * responsiveScale}px`,
                            fontFamily: "monospace",
                            fontWeight: "700",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            boxShadow:
                              "0 6px 24px rgba(0, 0, 0, 0.4), 0 3px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
                            minWidth: `${56 * responsiveScale}px`,
                            minHeight: `${40 * responsiveScale}px`,
                          }}
                          onMouseEnter={(e) => {
                            const target = e.target as HTMLElement;
                            target.style.background =
                              "linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.7) 100%)";
                            target.style.boxShadow =
                              "0 10px 32px rgba(0, 0, 0, 0.6), 0 5px 16px rgba(0, 0, 0, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.35)";
                          }}
                          onMouseLeave={(e) => {
                            const target = e.target as HTMLElement;
                            target.style.background =
                              "linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.6) 100%)";
                            target.style.boxShadow =
                              "0 6px 24px rgba(0, 0, 0, 0.4), 0 3px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25)";
                          }}
                          title="Playback Speed"
                        >
                          {playbackRate}x
                        </button>

                        {showSpeedMenu && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "100%",
                              right: 0,
                              marginBottom: "12px",
                              background:
                                "linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(20, 20, 20, 0.95) 100%)",
                              backdropFilter: "blur(20px)",
                              borderRadius: "12px",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                              overflow: "hidden",
                              minWidth: "90px",
                              boxShadow:
                                "0 16px 48px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                            }}
                          >
                            {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map(
                              (speed) => (
                                <button
                                  key={speed}
                                  onClick={() =>
                                    handlePlaybackRateChange(speed)
                                  }
                                  style={{
                                    display: "block",
                                    width: "100%",
                                    padding: "10px 16px",
                                    background:
                                      playbackRate === speed
                                        ? "linear-gradient(135deg, rgba(99, 102, 241, 0.8) 0%, rgba(139, 92, 246, 0.6) 100%)"
                                        : "transparent",
                                    border: "none",
                                    color: "white",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontFamily: "monospace",
                                    fontWeight: "600",
                                    textAlign: "center",
                                    transition:
                                      "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                    borderBottom:
                                      speed !== 2
                                        ? "1px solid rgba(255, 255, 255, 0.05)"
                                        : "none",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (playbackRate !== speed) {
                                      (
                                        e.target as HTMLElement
                                      ).style.background =
                                        "linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (playbackRate !== speed) {
                                      (
                                        e.target as HTMLElement
                                      ).style.background = "transparent";
                                    }
                                  }}
                                >
                                  {speed}x
                                </button>
                              )
                            )}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          if (onFullscreenToggle) {
                            onFullscreenToggle();
                          } else if (playerRef.current) {
                            playerRef.current
                              .toggleFullscreen()
                              .catch((err) => {
                                console.error("Fullscreen error:", err);
                              });
                          }
                        }}
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.6) 100%)",
                          backdropFilter: "blur(10px)",
                          border: `${
                            2 * responsiveScale
                          }px solid rgba(255, 255, 255, 0.3)`,
                          color: "#ffffff",
                          cursor: "pointer",
                          padding: `${8 * responsiveScale}px`,
                          borderRadius: `${16 * responsiveScale}px`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow:
                            "0 6px 28px rgba(0, 0, 0, 0.4), 0 3px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
                          minWidth: `${44 * responsiveScale}px`,
                          minHeight: `${44 * responsiveScale}px`,
                        }}
                        onMouseEnter={(e) => {
                          const target = e.target as HTMLElement;
                          target.style.background =
                            "linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.7) 100%)";
                          target.style.boxShadow =
                            "0 10px 36px rgba(0, 0, 0, 0.6), 0 5px 16px rgba(0, 0, 0, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.35)";
                        }}
                        onMouseLeave={(e) => {
                          const target = e.target as HTMLElement;
                          target.style.background =
                            "linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.6) 100%)";
                          target.style.boxShadow =
                            "0 6px 28px rgba(0, 0, 0, 0.4), 0 3px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.25)";
                        }}
                        title={
                          isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"
                        }
                      >
                        {isFullscreen ? (
                          <FaCompress
                            size={Math.max(14, 16 * responsiveScale)}
                            style={{ filter: "drop-shadow(0 0 0 transparent)" }}
                          />
                        ) : (
                          <FaExpand
                            size={Math.max(14, 16 * responsiveScale)}
                            style={{ filter: "drop-shadow(0 0 0 transparent)" }}
                          />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              showCustomControls &&
              !showLicenseWarning && (
                <div
                  style={{
                    position: "absolute",
                    bottom: `${10 * responsiveScale}px`,
                    right: `${10 * responsiveScale}px`,
                    transform: "none",
                    display: "flex",
                    flexDirection: isPortrait ? "column" : "row",
                    gap: `${10 * responsiveScale}px`,
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      padding: "8px",
                      margin: "-8px",
                    }}
                    onMouseEnter={() => setShowVolumeSlider(true)}
                    onMouseLeave={() => setShowVolumeSlider(false)}
                  >
                    <button
                      onClick={() => {
                        if (playerRef.current) {
                          playerRef.current.toggleMute();
                        }
                        if (onVolumeToggle) {
                          onVolumeToggle();
                        }
                      }}
                      onMouseEnter={(e) => {
                        const target = e.currentTarget as HTMLButtonElement;
                        target.style.boxShadow =
                          "0 14px 48px rgba(0, 0, 0, 0.7), 0 0 0 3px rgba(255, 255, 255, 0.8), inset 0 2px 0 rgba(255, 255, 255, 0.4)";
                        target.style.background =
                          "linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 100%)";
                      }}
                      onMouseLeave={(e) => {
                        const target = e.currentTarget as HTMLButtonElement;
                        target.style.boxShadow =
                          "0 10px 36px rgba(0, 0, 0, 0.6), 0 0 0 2px rgba(255, 255, 255, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                        target.style.background =
                          "linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.65) 100%)";
                      }}
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.65) 100%)",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: `${18 * responsiveScale}px`,
                        padding: `${8 * responsiveScale}px`,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backdropFilter: "blur(20px)",
                        boxShadow:
                          "0 10px 36px rgba(0, 0, 0, 0.6), 0 0 0 2px rgba(255, 255, 255, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        minWidth: `${46 * responsiveScale}px`,
                        minHeight: `${46 * responsiveScale}px`,
                      }}
                      title={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted || volume === 0 ? (
                        <FaVolumeMute
                          size={Math.max(14, 16 * responsiveScale)}
                          style={{
                            filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8))",
                            color: "#ffffff",
                          }}
                        />
                      ) : volume < 0.5 ? (
                        <FaVolumeDown
                          size={Math.max(14, 16 * responsiveScale)}
                          style={{
                            filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8))",
                            color: "#ffffff",
                          }}
                        />
                      ) : (
                        <FaVolumeUp
                          size={Math.max(14, 16 * responsiveScale)}
                          style={{
                            filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8))",
                            color: "#ffffff",
                          }}
                        />
                      )}
                    </button>

                    {showVolumeSlider && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            bottom: "100%",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "60px",
                            height: "20px",
                            marginBottom: "-16px",
                            zIndex: 9,
                          }}
                          onMouseEnter={() => setShowVolumeSlider(true)}
                          onMouseLeave={() => setShowVolumeSlider(false)}
                        />
                        <div
                          style={{
                            position: "absolute",
                            bottom: "100%",
                            left: "50%",
                            transform: "translateX(-50%)",
                            marginBottom: "4px",
                            background:
                              "linear-gradient(135deg, rgba(0, 0, 0, 0.96) 0%, rgba(20, 20, 20, 0.92) 100%)",
                            backdropFilter: "blur(20px)",
                            padding: "10px 14px",
                            borderRadius: "14px",
                            border: "2px solid rgba(255, 255, 255, 0.7)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "128px",
                            boxShadow:
                              "0 12px 40px rgba(0, 0, 0, 0.85), 0 4px 12px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.35)",
                            zIndex: 10,
                            transition:
                              "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
                          }}
                          onMouseEnter={(e) => {
                            setShowVolumeSlider(true);
                            e.currentTarget.style.boxShadow =
                              "0 16px 48px rgba(0, 0, 0, 0.9), 0 6px 16px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.4), 0 0 24px rgba(96, 165, 250, 0.4)";
                            e.currentTarget.style.borderColor =
                              "rgba(96, 165, 250, 0.8)";
                          }}
                          onMouseLeave={(e) => {
                            setShowVolumeSlider(false);
                            e.currentTarget.style.boxShadow =
                              "0 12px 40px rgba(0, 0, 0, 0.85), 0 4px 12px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.35)";
                            e.currentTarget.style.borderColor =
                              "rgba(255, 255, 255, 0.7)";
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              width: "8px",
                              height: "104px",
                              cursor: "pointer",
                              transition: "transform 0.2s ease-in-out",
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              const sliderElement = e.currentTarget;

                              const handleMouseMove = (
                                moveEvent: MouseEvent
                              ) => {
                                if (!sliderElement) return;
                                const rect =
                                  sliderElement.getBoundingClientRect();
                                const y = moveEvent.clientY - rect.top;
                                const percentage =
                                  1 - Math.max(0, Math.min(1, y / rect.height));
                                handleVolumeChange(percentage);
                              };

                              const handleMouseUp = () => {
                                document.removeEventListener(
                                  "mousemove",
                                  handleMouseMove
                                );
                                document.removeEventListener(
                                  "mouseup",
                                  handleMouseUp
                                );
                              };

                              document.addEventListener(
                                "mousemove",
                                handleMouseMove
                              );
                              document.addEventListener(
                                "mouseup",
                                handleMouseUp
                              );

                              const rect =
                                sliderElement.getBoundingClientRect();
                              const y = e.clientY - rect.top;
                              const percentage =
                                1 - Math.max(0, Math.min(1, y / rect.height));
                              handleVolumeChange(percentage);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              const y = e.clientY - rect.top;
                              const percentage =
                                1 - Math.max(0, Math.min(1, y / rect.height));
                              handleVolumeChange(percentage);
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                bottom: "0",
                                left: "0",
                                width: "100%",
                                height: "100%",
                                background:
                                  "linear-gradient(180deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.5) 100%)",
                                borderRadius: "4px",
                                border: "1px solid rgba(255, 255, 255, 0.4)",
                                boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.3)",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                bottom: "0",
                                left: "0",
                                width: "100%",
                                height: `${(isMuted ? 0 : volume) * 100}%`,
                                background:
                                  "linear-gradient(180deg, rgba(125, 211, 252, 1) 0%, rgba(96, 165, 250, 0.98) 50%, rgba(59, 130, 246, 0.95) 100%)",
                                borderRadius: "4px",
                                transition:
                                  "height 0.15s ease-out, box-shadow 0.2s ease-in-out",
                                boxShadow:
                                  "0 0 12px rgba(96, 165, 250, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.4)",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                bottom: `calc(${
                                  (isMuted ? 0 : volume) * 100
                                }% - 8px)`,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "16px",
                                height: "16px",
                                background:
                                  "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                                borderRadius: "50%",
                                border: "2px solid rgba(96, 165, 250, 0.9)",
                                boxShadow:
                                  "0 3px 8px rgba(0, 0, 0, 0.5), 0 0 0 2px rgba(96, 165, 250, 0.4), 0 0 16px rgba(96, 165, 250, 0.5)",
                                transition:
                                  "bottom 0.15s ease-out, transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, width 0.2s ease-in-out, height 0.2s ease-in-out",
                                cursor: "grab",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow =
                                  "0 4px 12px rgba(0, 0, 0, 0.6), 0 0 0 3px rgba(96, 165, 250, 0.6), 0 0 24px rgba(96, 165, 250, 0.7)";
                                e.currentTarget.style.cursor = "grab";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow =
                                  "0 3px 8px rgba(0, 0, 0, 0.5), 0 0 0 2px rgba(96, 165, 250, 0.4), 0 0 16px rgba(96, 165, 250, 0.5)";
                              }}
                              onMouseDown={(e) => {
                                e.currentTarget.style.cursor = "grabbing";
                              }}
                              onMouseUp={(e) => {
                                e.currentTarget.style.cursor = "grab";
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      if (onFullscreenToggle) {
                        onFullscreenToggle();
                      } else if (playerRef.current) {
                        playerRef.current.toggleFullscreen().catch((err) => {
                          console.error("Fullscreen error:", err);
                        });
                      }
                    }}
                    onMouseEnter={(e) => {
                      const target = e.currentTarget as HTMLButtonElement;
                      target.style.boxShadow =
                        "0 14px 48px rgba(0, 0, 0, 0.7), 0 0 0 3px rgba(255, 255, 255, 0.8), inset 0 2px 0 rgba(255, 255, 255, 0.4)";
                      target.style.background =
                        "linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 100%)";
                    }}
                    onMouseLeave={(e) => {
                      const target = e.currentTarget as HTMLButtonElement;
                      target.style.boxShadow =
                        "0 10px 36px rgba(0, 0, 0, 0.6), 0 0 0 2px rgba(255, 255, 255, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                      target.style.background =
                        "linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.65) 100%)";
                    }}
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.65) 100%)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: `${18 * responsiveScale}px`,
                      padding: `${8 * responsiveScale}px`,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(20px)",
                      boxShadow:
                        "0 10px 36px rgba(0, 0, 0, 0.6), 0 0 0 2px rgba(255, 255, 255, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      minWidth: `${46 * responsiveScale}px`,
                      minHeight: `${46 * responsiveScale}px`,
                    }}
                    title={
                      isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"
                    }
                  >
                    {isFullscreen ? (
                      <FaCompress
                        size={Math.max(14, 16 * responsiveScale)}
                        style={{
                          filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8))",
                          color: "#ffffff",
                        }}
                      />
                    ) : (
                      <FaExpand
                        size={Math.max(14, 16 * responsiveScale)}
                        style={{
                          filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.8))",
                          color: "#ffffff",
                        }}
                      />
                    )}
                  </button>
                </div>
              )
            )}

            {onControlClick && (
              <div
                onClick={onControlClick}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1,
                  cursor: "pointer",
                }}
              />
            )}
          </div>
        </>
      );
    },
    (prevProps, nextProps) => {
      for (const prop of CRITICAL_PROPS) {
        if ((prevProps as any)[prop] !== (nextProps as any)[prop]) {
          return false;
        }
      }

      const uiProps = [
        "autoplay",
        "muted",
        "controls",
        "showCustomControls",
        "className",
        "style",
        "wrapperClassName",
        "wrapperStyle",
        "playsInline",
        "preload",
        "poster",
        "children",
      ] as const;

      for (const prop of uiProps) {
        if ((prevProps as any)[prop] !== (nextProps as any)[prop]) {
          return false;
        }
      }

      const callbackProps = [
        "onReady",
        "onVolumeToggle",
        "onFullscreenToggle",
        "onControlClick",
      ] as const;
      for (const prop of callbackProps) {
        if ((prevProps as any)[prop] !== (nextProps as any)[prop]) {
          return false;
        }
      }

      return true;
    }
  );
